import React from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useStripeStatus, openStripeFix, demoAllowed, cycleDemo, setDemo, DEMO_LABEL } from './stripeStatus';
import './StripeStatus.css';

export const StripeMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
    <path d="M7 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
export const WarnMark = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

/** Status chip. In dev, staging and beta a click steps to the next demo state. */
export function StatusChip({ tone, children }) {
  if (!demoAllowed()) return <span className={`ss-pill is-${tone}`}>{children}</span>;
  // A span, not a button: the hub row that holds this chip is itself a button.
  const go = (e) => { e.preventDefault(); e.stopPropagation(); cycleDemo(); };
  return (
    <span role="button" tabIndex={0} className={`ss-pill ss-pill--demo is-${tone}`} onClick={go} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(e); }} title="Demo: click to see the next status">
      {children}
    </span>
  );
}

/** Small marker when a demo state is showing, with a way back to the live one. */
export function DemoTag({ demo }) {
  if (!demo) return null;
  const reset = (e) => { e.preventDefault(); e.stopPropagation(); setDemo(''); };
  return <span role="button" tabIndex={0} className="ss-demo-tag" onClick={reset} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') reset(e); }} title="Back to the real status">demo · {DEMO_LABEL[demo]} · reset</span>;
}

/** "Stripe needs photo ID … and your website by 24 Sept." */
export const needsSentence = (s) => (s.needs && s.needs.length
  ? `Stripe needs ${s.needs.slice(0, 2).map((n) => n.charAt(0).toLowerCase() + n.slice(1)).join(' and ')}${s.needs.length > 2 ? ` and ${s.needs.length - 2} more` : ''}${s.deadline ? ` by ${s.deadline}` : ''}.`
  : '');

/**
 * Stripe account status on the Payouts and Payments pages.
 *   connected            one quiet line
 *   not connected        a card with "Add Stripe account"
 *   needs attention      a warning section you cannot miss, with "Fix now"
 *   verifying / review   an amber note, nothing to do yet
 * Pass `own` to reuse data the page already loaded (the Payouts page does).
 */
function StripeStatusCard({ own, onAction, history }) {
  const dispatch = useDispatch();
  const s = useStripeStatus(own);
  if (s.loading) return null;
  const act = () => (onAction ? onAction(s) : openStripeFix(dispatch, history, s));

  if (s.key === 'active') {
    return (
      <div className="ss-strip is-ok" role="status">
        <span className="ss-strip-icon" aria-hidden="true"><StripeMark /></span>
        <div className="ss-strip-body"><div className="ss-strip-line"><b>{s.label}</b><StatusChip tone="ok">Connected</StatusChip>{s.note && <span className="ss-strip-muted">{s.note}</span>}<DemoTag demo={s.demo} /></div></div>
      </div>
    );
  }

  if (s.key === 'not_connected' || s.key === 'unknown') {
    return (
      <div className={`ss-strip is-${s.tone}`} role="status">
        <span className="ss-strip-icon" aria-hidden="true"><StripeMark /></span>
        <div className="ss-strip-body">
          <div className="ss-strip-line"><b>{s.label}</b><StatusChip tone="off">{s.key === 'unknown' ? 'Unknown' : 'Not connected'}</StatusChip><DemoTag demo={s.demo} /></div>
          <p className="ss-strip-note">{s.note}</p>
        </div>
        {s.cta && <button type="button" className="ss-cta" onClick={s.key === 'unknown' ? () => window.location.reload() : act}>{s.cta}</button>}
      </div>
    );
  }

  const urgent = s.key === 'issue' || s.key === 'restricted';
  return (
    <section className={`ss-alert is-${s.tone}${urgent ? ' ss-alert--urgent' : ''}`} role={urgent ? 'alert' : 'status'} aria-live="polite">
      <div className="ss-alert-head">
        <span className="ss-alert-icon" aria-hidden="true">{urgent ? <WarnMark /> : <StripeMark />}</span>
        <div className="ss-alert-text">
          <div className="ss-alert-title-row">
            <h3 className="ss-alert-title">{urgent ? (s.key === 'restricted' ? 'Your Stripe account is paused' : 'Your Stripe account needs attention') : s.label}</h3>
            {s.chip && <StatusChip tone={s.tone}>{s.chip}</StatusChip>}
            <DemoTag demo={s.demo} />
          </div>
          <p className="ss-alert-note">{s.note}</p>
          {s.needs.length > 0 && (
            <ul className="ss-alert-needs">
              {s.needs.map((n) => <li key={n}>{n}</li>)}
              {s.deadline && <li className="ss-alert-deadline">Needed by {s.deadline}</li>}
            </ul>
          )}
        </div>
      </div>
      {urgent && (
        <div className="ss-alert-actions">
          {s.key === 'restricted'
            ? <a className="ss-cta ss-cta--lg" href="mailto:info@beuapp.com?subject=Stripe%20account%20paused">Contact support</a>
            : <button type="button" className="ss-cta ss-cta--lg" onClick={act}>Fix now</button>}
          <span className="ss-alert-hint">{s.key === 'restricted' ? 'We will take it up with Stripe for you.' : 'Opens Stripe with your account ready. Takes a few minutes.'}</span>
        </div>
      )}
    </section>
  );
}

export default withRouter(StripeStatusCard);

/** Small pill for lists (settings hub). */
export function StripeStatusPill() {
  const s = useStripeStatus();
  if (s.loading) return null;
  const short = { active: 'Connected', beu_review: 'Under review', verifying: 'Verifying', issue: 'Needs attention', restricted: 'Paused', not_connected: 'Not connected', unknown: '' }[s.key] || '';
  if (!short) return null;
  return <StatusChip tone={s.tone}>{short}</StatusChip>;
}
